import React from 'react';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

const AboutUs = () => {
  const stats = [
    { number: '10k+', label: 'Verified Listings' },
    { number: '50k+', label: 'Successful Matches' },
    { number: '100%', label: 'Secure Escrow' },
    { number: '24/7', label: 'Broker Supervision' }
  ];

  const pillars = [
    {
      title: 'Rental Hunting with Escrow Protection',
      description: 'Finding a rental shouldn’t mean risking your hard-earned money. You pay HireBroker securely upfront, and we hold the funds safely. The broker only gets paid after you are fully moved in and satisfied with the property.'
    },
    {
      title: 'Acquiring Premium Land',
      description: 'Skip the stress of land fraud. We bypass the freelance "street brokers" and connect you directly with vetted, legally cleared land brokers to ensure your investment is secure and properly documented.'
    },
    {
      title: 'Selling Property Seamlessly',
      description: 'Position your land or rental asset in front of qualified buyers. We match your property with the region’s top-performing brokers who actually have ready clients, closing deals in record time.'
    }
  ];

  const steps = [
    {
      tag: '01',
      title: 'Submit Request & Secure Funds',
      description: 'Tell us exactly what you need. For rental hunting, deposit your agent fee safely into our secure holding system.'
    },
    {
      tag: '02',
      title: 'Elite Broker Matching',
      description: 'We assign your task to the highest-rated property broker in that specific neighborhood or land zone.'
    },
    {
      tag: '03',
      title: 'Inspection & Completion',
      description: 'You inspect the rental or land. Once you give the official green light, we release the payment to the broker.'
    }
  ];

  return (
    <div className="min-h-screen bg-white text-neutral-900 selection:bg-red-50 selection:text-red-900">
      <Header />
      
      <main className="pt-24">
        
        {/* SECTION 1: HERO (Pristine White Background) */}
        <section className="bg-white tracking-tight">
          <div className="max-w-6xl mx-auto px-6 pt-16 pb-24">
            <div className="mb-8 opacity-70">
              <Breadcrumbs />
            </div>
            
            <div className="max-w-3xl">
              <span className="text-xs uppercase tracking-[0.2em] text-red-600 font-semibold block mb-4">
                About HireBroker
              </span>
              <h1 className="text-4xl sm:text-6xl font-light text-neutral-950 leading-[1.1] mb-8">
                Your secure gateway to verified <span className="font-serif italic text-neutral-500 block sm:inline">rentals and land.</span>
              </h1>
              <p className="text-lg sm:text-xl text-neutral-500 font-light leading-relaxed max-w-2xl">
                We bridge the gap between property seekers and honest real estate brokers. By introducing transaction security to East African real estate, we ensure you never lose money to a fake agent again.
              </p>
            </div>
          </div>
        </section>


        {/* SECTION 2: STATS MATRIX (Soft Chalk Grey Background for Visual Break) */}
        <section className="bg-neutral-50 border-y border-neutral-200/60">
          <div className="max-w-6xl mx-auto px-6 py-16">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-4">
              {stats.map((stat, index) => (
                <div key={index} className="sm:border-l sm:border-neutral-200 sm:first:border-l-0 sm:pl-6 first:pl-0">
                  <div className="text-4xl font-light text-red-600 mb-2">
                    {stat.number}
                  </div>
                  <div className="text-xs uppercase tracking-wider text-neutral-500 font-medium">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>


        {/* SECTION 3: THE DEEPER EXPLANATION (Pristine White Background) */}
        <section className="bg-white py-24 max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <span className="text-xs uppercase tracking-[0.2em] text-red-600 font-semibold block mb-4">
                The Trust Paradigm
              </span>
              <h2 className="text-3xl font-light text-neutral-950 tracking-tight leading-tight">
                Real estate in East Africa has a trust problem. We are the architectural solution.
              </h2>
            </div>
            
            <div className="lg:col-span-7 space-y-8 text-neutral-500 font-light leading-relaxed text-base sm:text-lg">
              <p>
                In East Africa, the journey to finding a rental house or purchasing verified land is often plagued by fragmentation, lack of transparency, and the financial risk of dealing with unverified street agents. Every year, thousands of Rental seekers lose money to fake agents <strong>(brokers)</strong>, dual-committed listings, and phantom properties.
              </p>
              <p>
                <strong className="text-neutral-900 font-medium">HireBroker completely changes this dynamic.</strong> We act as both a protective layer and an intelligent matching engine. Instead of wandering through neighborhoods or risking capital on unverified listings, Rental seekers deposit their searching or hunting fees directly to HireBroker—not the agent. 
              </p>
              <p>
                We securely hold these funds in escrow and only release the payment to the assigned broker <span className="font-serif italic text-neutral-900">after</span> you have physically inspected the property, verified its legal authenticity, and successfully moved in or signed the official deeds. If the broker doesn’t deliver, your money remains completely safe.
              </p>
            </div>
          </div>
        </section>


        {/* SECTION 4: CORE CAPABILITIES (Soft Chalk Grey Background) */}
        <section className="bg-neutral-50 border-y border-neutral-200/60 py-24">
          <div className="max-w-6xl mx-auto px-6">
            <div className="mb-16">
              <span className="text-xs uppercase tracking-[0.2em] text-red-600 font-semibold block mb-2">
                What We Do
              </span>
              <h2 className="text-3xl font-light text-neutral-950 tracking-tight">Our Core Framework</h2>
            </div>

            <div className="grid lg:grid-cols-3 gap-12 lg:gap-8">
              {pillars.map((pillar, index) => (
                <div key={index} className="space-y-4">
                  <h3 className="text-xl font-medium text-neutral-950 tracking-tight border-b border-neutral-200 pb-3">
                    {pillar.title}
                  </h3>
                  <p className="text-sm text-neutral-500 font-light leading-relaxed">
                    {pillar.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>


        {/* SECTION 5: THE ECOSYSTEM FLOW (Warm Muted Sand/Grey Background) */}
        <section className="bg-neutral-100/70 py-24">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid lg:grid-cols-12 gap-12 items-start">
              <div className="lg:col-span-4">
                <span className="text-xs uppercase tracking-[0.2em] text-neutral-400 font-semibold block mb-2">
                  The Execution
                </span>
                <h2 className="text-3xl font-light text-neutral-950 tracking-tight">
                  How HireBroker guarantees your security.
                </h2>
                <p className="text-sm text-neutral-500 font-light mt-4 leading-relaxed">
                  We don't just hand you a phone number. We govern the entire transaction from the moment you start searching until the paperwork is safely signed.
                </p>
              </div>
              
              <div className="lg:col-span-8 space-y-8">
                {steps.map((step, index) => (
                  <div key={index} className="flex gap-6 items-start bg-white p-6 border border-neutral-200/40 shadow-sm">
                    <span className="text-xs font-mono font-bold text-red-600 pt-1">
                      {step.tag}
                    </span>
                    <div>
                      <h3 className="font-medium text-neutral-950 text-base mb-1">{step.title}</h3>
                      <p className="text-sm text-neutral-500 font-light leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>


        {/* SECTION 6: CTA (Deep Charcoal / Black Background for Powerful End Note) */}
        <section className="bg-neutral-950 text-white py-24">
          <div className="max-w-4xl mx-auto px-6 text-center space-y-8">
            <span className="text-xs uppercase tracking-[0.2em] text-red-500 font-semibold block">
              Get Started
            </span>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-white">
              Ready to find your next space safely?
            </h2>
            <p className="text-neutral-400 font-light max-w-xl mx-auto text-sm sm:text-base">
              Connect with vetted local brokers who prioritize clarity, verification, and legal integrity.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <a
                href="/property-search-browse"
                className="inline-flex items-center justify-center px-6 py-3 bg-red-600 text-white text-sm font-medium tracking-tight hover:bg-red-700 transition-colors duration-200"
              >
                Start Rent Hunting
              </a>
              <a
                href="/contact"
                className="inline-flex items-center justify-center px-6 py-3 border border-neutral-800 text-neutral-300 text-sm font-medium tracking-tight hover:bg-neutral-900 transition-colors duration-200"
              >
                Buy / Sell Land
              </a>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default AboutUs;